import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Popper,
  PopperProps,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ILegalClientAddress,
  initAddress,
  legalClientAddressSchema,
} from '../../Interfaces/ILegalClientAddress';
import { useAlert } from '../../context/AlertContext';
import type { State } from '../../redux/mainReducer';
import { PostcodeSearch } from './PostcodeSearch';

const CustomPopper = (props: PopperProps) => {
  return <Popper {...props} placement='top' />;
};

const GERMANY_COUNTRY_ID = 1;

interface IProps {
  unmount: () => void;
  onSave: (address: ILegalClientAddress) => void;
  selectedAddress?: ILegalClientAddress;
}

export const UpdateOrCreateAddressDialog = ({
  unmount,
  onSave,
  selectedAddress,
}: IProps) => {
  let audio = new Audio("/wasser.mp3")

  const [formValues, setFormValues] = useState<ILegalClientAddress>(
    selectedAddress ?? initAddress()
  );

  const addressTypes = useSelector(
    (state: State) => state.addressTypeArray.storeAddressType
  );

  const countries = useSelector(
    (state: State) => state.countryArray.storeCountry
  );

  const federalStates = useSelector(
    (state: State) => state.federalStateArray.storeFederalState
  );

  const { showAlert } = useAlert();

  useEffect(()=> {
    if ((formValues.Street != undefined && formValues.Street === "Altschauerberg")
      && (formValues.Number != undefined && formValues.Number === "8")
      && (formValues.idPostcode != undefined && formValues.idPostcode >= 129097 && formValues.idPostcode <= 129122) 
    ) {
      console.log("eine runde forsa zockne")
      var playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.then(_ => {
          // Automatic playback started!
          // Show playing UI.
        })
        .catch(error => {
          // Auto-play was prevented
          // Show paused UI.
        });}
    }
  },[formValues])

  const onSubmit = () => {
    const { data, success, error } =
      legalClientAddressSchema.safeParse(formValues);

    if (success) {
      onSave(data);
      unmount();
    } else {
      console.error(error);
      showAlert({
        text: 'Bitte füllen Sie alle Felder aus',
        severity: 'error',
      });
    }
  };

  const postcodeObject =
    formValues.idPostcode > 0
      ? {
          idPostcode: formValues.idPostcode,
          Postcode: formValues.Postcode,
          City: formValues.City,
          idCountry: formValues.idCountry,
        }
      : undefined;

  return (
    <Dialog
      onClose={unmount}
      open={true}
      fullWidth
      maxWidth='md'
      scroll='paper'
    >
      <DialogTitle>
        {!selectedAddress ? 'Adresse hinzufügen' : 'Adresse bearbeiten'}
      </DialogTitle>
      <DialogContent sx={{ minHeight: '40vh' }}>
        <Grid container spacing={2} pt={1}>
          <Grid item xs={12}>
            <TextField
              select
              required
              label='Adressart'
              size='small'
              value={formValues.idAddresstype || addressTypes[0].idAddresstype}
              fullWidth
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({
                  ...formValues,
                  idAddresstype: Number(event.target.value),
                })
              }
            >
              {addressTypes.map(at => (
                <MenuItem key={at.idAddresstype} value={at.idAddresstype}>
                  {at.Addresstype}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={8}>
            <TextField
              label='Straße'
              id='street'
              size='small'
              value={formValues.Street}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({
                  ...formValues,
                  Street: event.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label='Hausnummer'
              id='street-number'
              size='small'
              value={formValues.Number}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({
                  ...formValues,
                  Number: event.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Adresszusatz'
              id='street-additional'
              size='small'
              value={formValues.StreetAdditional}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({
                  ...formValues,
                  StreetAdditional: event.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <PostcodeSearch
              postcodeObject={postcodeObject}
              required={true}
              updatePostcodeObject={value => {
                if (!value) return;

                setFormValues({
                  ...formValues,
                  idPostcode: value.idPostcode!,
                  Postcode: value.Postcode,
                  City: value.City,
                  idCountry: value.idCountry,
                  Country: countries.find(c => c.idCountry === value.idCountry)!
                    .Country,
                });
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Autocomplete
              disablePortal
              options={countries}
              fullWidth
              size='small'
              getOptionKey={option => option.idCountry}
              getOptionLabel={option => option.Country}
              disabled
              renderInput={params => (
                <TextField
                  required
                  {...params}
                  label='Land'
                  placeholder='Land auswählen'
                />
              )}
              PopperComponent={CustomPopper}
              value={
                countries.find(c => c.idCountry === formValues.idCountry) ??
                null
              }
              onChange={(_, value) => {
                if (!value) {
                  setFormValues({
                    ...formValues,
                    Country: '',
                    idCountry: 0,
                    FederalState: '',
                    idFederalState: 0,
                  });
                  return;
                }

                const country = countries.find(
                  c => c.idCountry === value?.idCountry
                );

                setFormValues({
                  ...formValues,
                  Country: country!.Country,
                  idCountry: country!.idCountry,
                  FederalState: '',
                  idFederalState: 0,
                });
              }}
            />
          </Grid>
          {formValues.idCountry === GERMANY_COUNTRY_ID && (
            <Grid item xs={6}>
              <TextField
                label='Bundesland'
                select
                id='federal-state'
                size='small'
                fullWidth
                value={formValues.FederalState}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const federalState = federalStates.find(
                    f => f.FederalState === event.target.value
                  );

                  setFormValues({
                    ...formValues,
                    FederalState: event.target.value,
                    idFederalState: federalState!.idFederalState,
                  });
                }}
              >
                {federalStates.map(federalState => (
                  <MenuItem
                    key={federalState.idFederalState}
                    value={federalState.FederalState}
                  >
                    {federalState.FederalState}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={unmount}>
          Abbrechen
        </Button>
        <Button onClick={onSubmit} variant='contained'>
          {!!selectedAddress ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
